import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { InvoiceAPI, formatCurrency } from "../lib/api";
import { useCenturion } from "../context/CenturionContext";
import { useCompany } from "../context/CompanyContext";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, ChevronDown, Lock } from "lucide-react";

const blankItem = () => ({ description: "", detail: "", hours: "", rate: "", amount: 0, fixed: false });
const blankAdj = () => ({ label: "", amount: 0, is_credit: false });

const blankInvoice = (company) => ({
  invoice_number: "",
  title: "",
  project_name: "",
  invoice_date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
  due_date: "",
  period: "",
  to_party: { company: "", address: "", line2: "" },
  line_items: [blankItem()],
  adjustments: [],
  subtotal: 0,
  total: 0,
  currency: company.default_currency,
  status: "sent",
  notes: "",
  payment_method: company.payment_instructions,
  contact_email: company.contact_email,
  project_stats: "",
  project_summary: "",
});

const InvoiceFormPage = ({ mode = "create" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { unlocked } = useCenturion();
  const { company } = useCompany();
  const [form, setForm] = useState(() => blankInvoice(company));
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!unlocked) {
      toast.error("Issuer mode locked. Type 'centurion' anywhere to unlock.");
      navigate("/");
      return;
    }
    if (mode === "edit" && id) {
      (async () => {
        try {
          const data = await InvoiceAPI.get(id);
          // Normalize empty arrays
          setForm({
            ...blankInvoice(company),
            ...data,
            line_items: data.line_items?.length ? data.line_items : [blankItem()],
            adjustments: data.adjustments || [],
          });
        } catch {
          toast.error("Failed to load invoice");
          navigate("/");
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [mode, id, navigate, unlocked, company]);

  // Recompute subtotal & total live
  const computed = useMemo(() => {
    const sub = (form.line_items || []).reduce((sum, it) => {
      const amt = Number(it.amount) || 0;
      return sum + amt;
    }, 0);
    const adj = (form.adjustments || []).reduce(
      (sum, a) => sum + (a.is_credit ? -1 : 1) * (Number(a.amount) || 0),
      0
    );
    return { subtotal: sub, total: sub + adj };
  }, [form.line_items, form.adjustments]);

  const setField = (path, value) => {
    setForm((prev) => {
      const out = { ...prev };
      const keys = path.split(".");
      let target = out;
      for (let i = 0; i < keys.length - 1; i++) {
        target[keys[i]] = { ...target[keys[i]] };
        target = target[keys[i]];
      }
      target[keys[keys.length - 1]] = value;
      return out;
    });
  };

  const updateItem = (idx, patch) => {
    setForm((prev) => {
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], ...patch };
      // Auto-calc amount when hours+rate present (and not fixed)
      const it = items[idx];
      if (!it.fixed && it.hours && it.rate) {
        items[idx].amount = Math.round(Number(it.hours) * Number(it.rate) * 100) / 100;
      }
      return { ...prev, line_items: items };
    });
  };

  const addItem = () => setForm((p) => ({ ...p, line_items: [...p.line_items, blankItem()] }));
  const removeItem = (idx) =>
    setForm((p) => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }));

  const updateAdj = (idx, patch) => {
    setForm((prev) => {
      const next = [...prev.adjustments];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, adjustments: next };
    });
  };
  const addAdj = () => setForm((p) => ({ ...p, adjustments: [...(p.adjustments || []), blankAdj()] }));
  const removeAdj = (idx) =>
    setForm((p) => ({ ...p, adjustments: p.adjustments.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.invoice_number || !form.project_name || !form.to_party?.company) {
      toast.error("Invoice #, Project, and Bill-To Company are required");
      return;
    }
    setSaving(true);

    // Sanitize line items: convert string number fields to numbers, drop empty
    const cleaned = {
      ...form,
      subtotal: computed.subtotal,
      total: computed.total,
      line_items: form.line_items
        .filter((i) => i.description?.trim())
        .map((i) => ({
          description: i.description,
          detail: i.detail || null,
          hours: i.hours === "" || i.hours == null ? null : Number(i.hours),
          rate: i.rate === "" || i.rate == null ? null : Number(i.rate),
          amount: Number(i.amount) || 0,
          fixed: !!i.fixed,
        })),
      adjustments: (form.adjustments || [])
        .filter((a) => a.label?.trim())
        .map((a) => ({ label: a.label, amount: Number(a.amount) || 0, is_credit: !!a.is_credit })),
    };

    try {
      if (mode === "create") {
        const created = await InvoiceAPI.create(cleaned);
        toast.success("Invoice created");
        navigate(`/invoices/${created.id}`);
      } else {
        await InvoiceAPI.update(id, cleaned);
        toast.success("Invoice updated");
        navigate(`/invoices/${id}`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Save failed — check console for details");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 py-12 animate-pulse">
          <div className="h-6 w-40 bg-white/5 rounded mb-6" />
          <div className="bg-white/5 h-[600px] rounded-lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link to="/" className="text-[13px] text-white/60 hover:text-white flex items-center gap-1.5 mb-6">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b] mb-2">
              {mode === "create" ? "Create" : "Edit"}
            </div>
            <h1 className="text-[32px] md:text-[36px] font-semibold tracking-tight">
              {mode === "create" ? "New Invoice" : `Edit ${form.invoice_number}`}
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Meta section */}
          <Section title="Invoice Details">
            <Row>
              <Field label="Invoice Number" required>
                <input className="field" value={form.invoice_number} onChange={(e) => setField("invoice_number", e.target.value)} placeholder={`${company.invoice_number_prefix}-005`} />
              </Field>
              <Field label="Status">
                <Select
                  value={form.status}
                  onChange={(v) => setField("status", v)}
                  options={[
                    { value: "draft", label: "Draft" },
                    { value: "sent", label: "Sent" },
                    { value: "paid", label: "Paid" },
                    { value: "overdue", label: "Overdue" },
                  ]}
                />
              </Field>
            </Row>
            <Field label="Title">
              <input className="field" value={form.title || ""} onChange={(e) => setField("title", e.target.value)} placeholder="Project title shown in dashboard" />
            </Field>
            <Field label="Project Name" required>
              <input className="field" value={form.project_name} onChange={(e) => setField("project_name", e.target.value)} placeholder="Long project description" />
            </Field>
            <Row>
              <Field label="Invoice Date">
                <input className="field" value={form.invoice_date} onChange={(e) => setField("invoice_date", e.target.value)} placeholder="April 25, 2026" />
              </Field>
              <Field label="Due Date">
                <input className="field" value={form.due_date || ""} onChange={(e) => setField("due_date", e.target.value)} placeholder="May 15, 2026" />
              </Field>
            </Row>
            <Field label="Invoice Period (optional)">
              <input className="field" value={form.period || ""} onChange={(e) => setField("period", e.target.value)} placeholder="May 2024 — March 2026 (22 months)" />
            </Field>
            <Field label="Project Stats Banner (optional)">
              <input className="field" value={form.project_stats || ""} onChange={(e) => setField("project_stats", e.target.value)} placeholder="17 source files | 1,122 lines of code | React 18 + …" />
            </Field>
          </Section>

          {/* Bill To */}
          <Section title="Bill To">
            <Field label="Company" required>
              <input className="field" value={form.to_party.company} onChange={(e) => setField("to_party.company", e.target.value)} placeholder="Northwest Media Ltd" />
            </Field>
            <Row>
              <Field label="Address">
                <input className="field" value={form.to_party.address || ""} onChange={(e) => setField("to_party.address", e.target.value)} placeholder="1398 Bayview Ave" />
              </Field>
              <Field label="City / Postal">
                <input className="field" value={form.to_party.line2 || ""} onChange={(e) => setField("to_party.line2", e.target.value)} placeholder="Toronto, ON M4G 3A7" />
              </Field>
            </Row>
          </Section>

          {/* Line items */}
          <Section
            title="Line Items"
            action={
              <button type="button" onClick={addItem} className="btn-ghost text-[12px] py-1.5 px-3">
                <Plus size={13} /> Add line
              </button>
            }
          >
            <div className="space-y-3">
              {form.line_items.map((item, idx) => (
                <div key={idx} className="bg-white/[0.03] border border-white/8 rounded-md p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1">
                      <Field label={`Item ${idx + 1} — Description`}>
                        <input className="field" value={item.description} onChange={(e) => updateItem(idx, { description: e.target.value })} placeholder="Service description" />
                      </Field>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      className="mt-7 p-2 rounded-md text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      aria-label="Remove line"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <Field label="Detail (sub-text)">
                    <textarea rows={2} className="field" value={item.detail || ""} onChange={(e) => updateItem(idx, { detail: e.target.value })} placeholder="Detailed description of the service" />
                  </Field>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 items-end">
                    <Field label="Hours">
                      <input
                        className="field"
                        type="number"
                        step="0.25"
                        value={item.hours ?? ""}
                        disabled={item.fixed}
                        onChange={(e) => updateItem(idx, { hours: e.target.value })}
                      />
                    </Field>
                    <Field label="Rate ($)">
                      <input
                        className="field"
                        type="number"
                        step="0.01"
                        value={item.rate ?? ""}
                        disabled={item.fixed}
                        onChange={(e) => updateItem(idx, { rate: e.target.value })}
                      />
                    </Field>
                    <Field label={item.fixed ? "Amount ($)" : "Amount ($)"}>
                      <input
                        className="field"
                        type="number"
                        step="0.01"
                        value={item.amount ?? 0}
                        onChange={(e) => updateItem(idx, { amount: e.target.value })}
                      />
                    </Field>
                    <label className="flex items-center gap-2 pb-2.5 text-[12px] text-white/70 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!item.fixed}
                        onChange={(e) => updateItem(idx, { fixed: e.target.checked, hours: e.target.checked ? null : item.hours, rate: e.target.checked ? null : item.rate })}
                      />
                      Fixed price
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Adjustments */}
          <Section
            title="Adjustments / Add-ons (optional)"
            action={
              <button type="button" onClick={addAdj} className="btn-ghost text-[12px] py-1.5 px-3">
                <Plus size={13} /> Add adjustment
              </button>
            }
          >
            {(form.adjustments?.length ?? 0) === 0 ? (
              <p className="text-white/40 text-[13px]">
                Add credits, deposits, fees, or extras (e.g. "Calendly Integration: $250", or "Deposit Paid: -$200").
              </p>
            ) : (
              <div className="space-y-3">
                {form.adjustments.map((adj, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px_44px] gap-3 items-end">
                    <Field label="Label">
                      <input className="field" value={adj.label} onChange={(e) => updateAdj(idx, { label: e.target.value })} placeholder="Calendly Integration" />
                    </Field>
                    <Field label="Amount ($)">
                      <input className="field" type="number" step="0.01" value={adj.amount} onChange={(e) => updateAdj(idx, { amount: e.target.value })} />
                    </Field>
                    <label className="flex items-center gap-2 pb-2.5 text-[12px] text-white/70 cursor-pointer">
                      <input type="checkbox" checked={adj.is_credit} onChange={(e) => updateAdj(idx, { is_credit: e.target.checked })} />
                      Credit (–)
                    </label>
                    <button
                      type="button"
                      onClick={() => removeAdj(idx)}
                      className="p-2 rounded-md text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-colors h-[40px]"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Notes */}
          <Section title="Terms, Notes & Summary">
            <Field label="Payment Method">
              <input className="field" value={form.payment_method || ""} onChange={(e) => setField("payment_method", e.target.value)} placeholder="E-Transfer to …" />
            </Field>
            <Field label="Note (appears under terms)">
              <textarea rows={3} className="field" value={form.notes || ""} onChange={(e) => setField("notes", e.target.value)} placeholder="A courtesy credit of $X has been applied…" />
            </Field>
            <Field label="Project Summary">
              <textarea rows={3} className="field" value={form.project_summary || ""} onChange={(e) => setField("project_summary", e.target.value)} />
            </Field>
          </Section>

          {/* Live totals + submit */}
          <div className="glass p-5 sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/45">Subtotal</div>
                <div className="text-[18px] font-semibold font-mono">{formatCurrency(computed.subtotal)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#d4a54b]">Total Due</div>
                <div className="text-[26px] font-bold font-mono text-[#e0bc62]">{formatCurrency(computed.total)}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/" className="btn-ghost">Cancel</Link>
              <button type="submit" disabled={saving} className="btn-primary">
                <Save size={14} /> {saving ? "Saving…" : mode === "create" ? "Create Invoice" : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

const Section = ({ title, children, action }) => (
  <section className="glass p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#d4a54b]">{title}</h3>
      {action}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const Row = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;

const Field = ({ label, required, children }) => (
  <div>
    <label className="field-label">
      {label} {required && <span className="text-[#d4a54b]">*</span>}
    </label>
    {children}
  </div>
);

const Select = ({ value, onChange, options }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="field appearance-none pr-9 cursor-pointer"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-[#0a0a0a]">{o.label}</option>
      ))}
    </select>
    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
  </div>
);

export default InvoiceFormPage;
